import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableFooter from '@material-ui/core/TableFooter';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import LastPageIcon from '@material-ui/icons/LastPage';
import {BASENAME} from "../../constants";
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';

import {ROLES} from '../Permissions';
import {actions} from '../../actions';

import DraggableDialog from '../parts/DraggableDialog';
import ChipsArray from '../parts/ChipsArray';
import SimpleDialogSelect from '../parts/SimpleDialogSelect';

import Loader from 'react-loader';
import {connect} from "react-redux";

const PER_PAGE = 10;
const COL_SPAN = 4;

const actionsStyles = theme => ({
    root: {
        flexShrink: 0,
        color: theme.palette.text.primary,
        marginLeft: theme.spacing.unit * 2.5,
    },
    button: {
        margin: theme.spacing.unit,
    },
    input: {
        display: 'none',
    },
});

class TablePaginationActions extends Component {
    handleFirstPageButtonClick = event => {
        this.props.onChangePage(event, 0);
    };

    handleBackButtonClick = event => {
        this.props.onChangePage(event, this.props.page - 1);
    };

    handleNextButtonClick = event => {
        this.props.onChangePage(event, this.props.page + 1);
    };

    handleLastPageButtonClick = event => {
        this.props.onChangePage(
            event,
            Math.max(0, Math.ceil(this.props.count / this.props.rowsPerPage) - 1),
        );
    };

    render() {
        const {classes, count, page, rowsPerPage, theme} = this.props;

        return (
            <div className={classes.root}>
                <IconButton
                    onClick={this.handleFirstPageButtonClick}
                    disabled={page === 0}
                    aria-label="First Page"
                >
                    {theme.direction === 'rtl' ? <LastPageIcon/> : <FirstPageIcon/>}
                </IconButton>
                <IconButton
                    onClick={this.handleBackButtonClick}
                    disabled={page === 0}
                    aria-label="Previous Page"
                >
                    {theme.direction === 'rtl' ? <KeyboardArrowRight/> : <KeyboardArrowLeft/>}
                </IconButton>
                <IconButton
                    onClick={this.handleNextButtonClick}
                    disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                    aria-label="Next Page"
                >
                    {theme.direction === 'rtl' ? <KeyboardArrowLeft/> : <KeyboardArrowRight/>}
                </IconButton>
                <IconButton
                    onClick={this.handleLastPageButtonClick}
                    disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                    aria-label="Last Page"
                >
                    {theme.direction === 'rtl' ? <FirstPageIcon/> : <LastPageIcon/>}
                </IconButton>
            </div>
        );
    }
}

TablePaginationActions.propTypes = {
    classes: PropTypes.object.isRequired,
    count: PropTypes.number.isRequired,
    onChangePage: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    theme: PropTypes.object.isRequired,
};

const TablePaginationActionsWrapped = withStyles(actionsStyles, {withTheme: true})(
    TablePaginationActions,
);

const styles = theme => ({
    root: {
        width: '100%',
        marginTop: theme.spacing.unit * 3,
    },
    table: {
        minWidth: 500,
    }
});

class CoordinatorsTable extends Component {
    state = {
        departments:['Loading departments...'],
        rows: [],
        rowsPerPage: 0,
        total: 0,
        page: 0,
        rowsPerPageOptions: [5, 10, 25],
        showTableFooter: false,
        loaded: true
    };

    async componentDidMount() {
        await this.fetchUsers(0, PER_PAGE);
        fetch(BASENAME + '/api/departments/undefined')
            .then(res=>res.json())
            .then(data=>{
                this.setState({
                    departments: data
                })
            })
    };

    onStart = () => {
        this.setState({loaded: false});
    };

    onSuccess = ({rows, total, page, rowsPerPage, showTableFooter, rowsPerPageOptions}) => {
        this.setState({
            rows,
            total,
            page,
            rowsPerPage,
            showTableFooter,
            rowsPerPageOptions,
            loaded: true});
    };

    onSuccessUpdateRoles = () => {
        this.setState({
            loaded: true});
    };

    onError = (err) => {
        this.setState({
            loaded: true
        });
        // handle error here
        console.log(err);
    };

    fetchUsers = async (page, rowsPerPage) => {
        this.onStart();
        try {
            // converting to 1 based pagination
            const page1Based = parseInt(page) + 1;
            const res = await fetch(BASENAME + '/api/users?page=' + page1Based + '&limit=' + rowsPerPage);
            const {users, total} = await res.json();
            const rows = users.map(user => {
                return {id: user._id, display_name: user.display_name, unid: user.unid, roles: user.roles, departments: user.departments};
            });
            let rowsPerPageOptions = this.state.rowsPerPageOptions;
            let showTableFooter = true;
            if (total < PER_PAGE) {
                rowsPerPage = parseInt(total);
                showTableFooter = false;
            }
            if (PER_PAGE < rowsPerPageOptions[0]) {
                rowsPerPageOptions.unshift(PER_PAGE);
            }
            this.onSuccess({rows, total, page, rowsPerPage, showTableFooter, rowsPerPageOptions});
        } catch(err) {
            this.onError(err);
        }
    };

    handleChangePage = async (event, page) => {
        await this.fetchUsers(page, this.state.rowsPerPage);
    };

    handleChangeRowsPerPage = async event => {
        await this.fetchUsers(this.state.page, parseInt(event.target.value));
    };

    handleRemoveUser = (unid) => async (event) => {
        // remove from DB
        const res = await fetch(BASENAME + '/api/users/' + unid, {method: "DELETE"});
        // re-fetch users with page 0
        await this.fetchUsers(0, PER_PAGE);
    };

    updateUser = async (unid, body) => {
        this.onStart();
        try {
            const res = await fetch(BASENAME + '/api/users/' + unid, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body)
            });
            this.onSuccessUpdateRoles();
        } catch(err) {
            this.onError(err);
        }
    };

    handleRemoveUserRole = (unid) => async (roles) => {
        const body = {roles:roles};
        await this.updateUser(unid, body);
        // re-fetch users with current page
        await this.fetchUsers(this.state.page, this.state.rowsPerPage);
    };

    handleAddUserRole = (unid, roles) => async (role) => {
        if(role) {
            roles.push(role);
            const body = {roles:roles};
            await this.updateUser(unid, body);
            // re-fetch users with current page
            await this.fetchUsers(this.state.page, this.state.rowsPerPage);
        }
    };

    handleAddUserDepartment = (unid, departments) => async (department) =>{
        if(department) {
            departments.push(department);
            const body = {departments:departments};
            await this.updateUser(unid, body);
            await this.fetchUsers(this.state.page, this.state.rowsPerPage);
        }
    };

    handleRemoveUserDepartment = (unid) => async (departments) =>{
        const body = {departments:departments};
        await this.updateUser(unid, body);
        await this.fetchUsers(this.state.page, this.state.rowsPerPage);
    };

    renderTableFooter() {
        const {rowsPerPage, page, total, rowsPerPageOptions} = this.state;
        return this.state.showTableFooter ? (
            <TableFooter>
                <TableRow>
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        colSpan={COL_SPAN}
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        SelectProps={{
                            native: true,
                        }}
                        onChangePage={this.handleChangePage}
                        onChangeRowsPerPage={this.handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActionsWrapped}
                    />
                </TableRow>
            </TableFooter>
        ) : null;
    }

    render() {
        const {classes} = this.props;
        const {rows, rowsPerPage, page, total} = this.state;
        const emptyRows = rowsPerPage - Math.min(rowsPerPage, total - page * rowsPerPage);
        return (
            <Paper className={classes.root}>
                    <Table className={classes.table}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>uNID</TableCell>
                                <TableCell>Manage User Roles</TableCell>
                                <TableCell>Departments</TableCell>
                                <TableCell>Delete User</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.display_name}</TableCell>
                                    <TableCell>{row.unid}</TableCell>
                                    <TableCell>
                                        <ChipsArray
                                            chips={row.roles.sort().map((role, ind) => {
                                                return {key: String(ind), label: role};
                                            })}
                                            onDelete={this.handleRemoveUserRole(row.unid)}
                                        >
                                            <SimpleDialogSelect
                                                data={Object.values(ROLES)}
                                                onClose={this.handleAddUserRole(row.unid, row.roles)}
                                            >
                                                <Fab size="small" color="secondary" aria-label="Add"
                                                     className={classes.margin}>
                                                    <AddIcon/>
                                                </Fab>
                                            </SimpleDialogSelect>
                                        </ChipsArray>
                                    </TableCell>
                                    <TableCell>
                                        <ChipsArray
                                            chips={row.departments.sort().map((department, ind) => {
                                                return {key: String(ind), label: department};
                                            })}
                                            onDelete={this.handleRemoveUserDepartment(row.unid)}
                                        >
                                            <SimpleDialogSelect
                                                data={this.state.departments}
                                                onClose={this.handleAddUserDepartment(row.unid, row.departments)}
                                            >
                                                <Fab size="small" color="secondary" aria-label="Add"
                                                     className={classes.margin}>
                                                    <AddIcon/>
                                                </Fab>
                                            </SimpleDialogSelect>
                                        </ChipsArray>
                                    </TableCell>
                                    <TableCell>
                                        <DraggableDialog
                                            contentTitle="Deleting User!"
                                            contentText={"Are you sure you want to delete user " + row.display_name + "?"}
                                            cancelText={"Cancel"}
                                            confirmText={"Delete"}
                                            onCancel={null}
                                            onConfirm={this.handleRemoveUser(row.unid)}
                                        >
                                            <IconButton className={classes.button} aria-label="Delete">
                                                <DeleteIcon/>
                                            </IconButton>
                                        </DraggableDialog>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {emptyRows > 0 && (
                                <TableRow style={{height: 48 * emptyRows}}>
                                    <TableCell colSpan={COL_SPAN}/>
                                </TableRow>
                            )}
                        </TableBody>
                        {this.renderTableFooter()}
                    </Table>
                <Loader loaded={this.state.loaded}/>
            </Paper>
        );
    }
}

CoordinatorsTable.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CoordinatorsTable);

